import { BaseCategory } from "./BaseCategory";
import { AuthoringBlockType, HeaderPositionType, ListStyleType, TextStyleType } from "../constants";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";
import { v4 as uuid } from "uuid";

export class AgendaCategory extends BaseCategory {
    get name() {
        return "Agenda";
    }

    get type() {
        return SlideCategories.AGENDA;
    }

    get icon() {
        return null;
    }

    get description() {
        return `The content specifically describes an agenda or meeting schedule.`;
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Parse the content to determine the agenda items or topics to be covered. List the main items in order. If there are sub-items or descriptions for each main item, include those. Separate any additional content into the conclusion`;
    }

    getImagePrompt() {
        return false;
    }

    getPromptSchema(options = {}) {
        return {
            items: this.getItemListSchema(options),
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    getSlideModels(model) {
        let models = [
            this.buildSingleColumnAgenda(model),
            // this.buildTwoColumnAgenda(model),
        ];

        return models;
    }

    buildSingleColumnAgenda(model) {
        let builder = new SlideModelBuilder("agenda", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        const blocks = [];

        // Create agenda items as numbered list blocks
        model.items.forEach(item => {
            // Main item block
            const mainBlock = {
                id: uuid(),
                type: AuthoringBlockType.TEXT,
                textStyle: TextStyleType.BULLET_LIST,
                listStyle: ListStyleType.NUMBERED,
                html: item.title,
                indent: 0,
            };

            blocks.push(mainBlock);

            // Add description/sub-items if present
            if (item.description) {
                const descriptionBlock = {
                    id: uuid(),
                    type: AuthoringBlockType.TEXT,
                    textStyle: TextStyleType.BULLET_LIST,
                    listStyle: ListStyleType.TEXT,
                    html: item.description,
                    indent: 1,
                };
                blocks.push(descriptionBlock);
            }
        });

        // Create a single column with all blocks
        const primary = {
            items: [
                {
                    id: uuid(),
                    isMedia: false,
                    text: {
                        blocks: blocks
                    }
                }
            ],
            autoDistributeBullets: false,
            autoArrangeColumnsCount: 1,
            showColumnHeaders: false,
            startNum: 1,
            listStyle: ListStyleType.NUMBERED,
        };

        builder.createPrimary(primary);

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }

    buildTwoColumnAgenda(model) {
        let builder = new SlideModelBuilder("agenda", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        const allBlocks = [];

        // Create all agenda items as numbered list blocks
        model.items.forEach(item => {
            // Main item block
            const mainBlock = {
                id: uuid(),
                type: AuthoringBlockType.TEXT,
                textStyle: TextStyleType.BULLET_LIST,
                listStyle: ListStyleType.NUMBERED,
                html: item.title,
                indent: 0,
            };

            if (item.linkToSlide) {
                mainBlock.linkToSlide = item.linkToSlide;
            }

            allBlocks.push(mainBlock);

            // Add description/sub-items if present
            if (item.description) {
                const descriptionBlock = {
                    id: uuid(),
                    type: AuthoringBlockType.TEXT,
                    textStyle: TextStyleType.BULLET_LIST,
                    listStyle: ListStyleType.TEXT,
                    html: item.description,
                    indent: 1,
                };
                allBlocks.push(descriptionBlock);
            }
        });

        // Split blocks into two columns (roughly equal distribution)
        const midpoint = Math.ceil(model.items.length / 2);
        let firstColumnBlockCount = 0;
        let secondColumnStart = 0;

        // Count blocks in first column (including main items and their descriptions)
        for (let i = 0; i < midpoint && i < model.items.length; i++) {
            firstColumnBlockCount++; // Main item
            if (model.items[i].description) {
                firstColumnBlockCount++; // Description
            }
        }

        const column1Blocks = allBlocks.slice(0, firstColumnBlockCount);
        const column2Blocks = allBlocks.slice(firstColumnBlockCount);

        const items = [];

        if (column1Blocks.length > 0) {
            items.push({
                id: uuid(),
                isMedia: false,
                text: {
                    blocks: column1Blocks
                }
            });
        }

        if (column2Blocks.length > 0) {
            items.push({
                id: uuid(),
                isMedia: false,
                text: {
                    blocks: column2Blocks
                }
            });
        }

        // If we only have one column worth of content, add an empty second column
        if (items.length === 1) {
            items.push({
                id: uuid(),
                isMedia: false,
                text: {
                    blocks: [
                        {
                            id: uuid(),
                            type: AuthoringBlockType.TEXT,
                            textStyle: TextStyleType.BULLET_LIST,
                            listStyle: ListStyleType.NUMBERED,
                            html: "",
                            indent: 0,
                        }
                    ]
                }
            });
        }

        const primary = {
            items: items,
            autoDistributeBullets: false,
            autoArrangeColumnsCount: 2,
            showColumnHeaders: false,
            startNum: 1,
            listStyle: ListStyleType.NUMBERED,
        };

        builder.createPrimary(primary);

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }
}
